import dayjs from 'dayjs';
import 'dayjs/locale/pl.js';

/**
 * Formatery daty i czasu do wyświetlania, nazwane po **formacie**, a nie po
 * ekranie, z którego wyrosły.
 *
 * Aplikacja mówi dziś dwoma zapisami daty: `DD MMM YYYY` („09 lis 2019”)
 * i `DD.MM.YYYY` („09.11.2019”). Tutaj mieszka pierwszy z nich — ten sam,
 * którym posługują się `utils/timeUtils.ts` i moduł zmian rezerwacji. Nazwa
 * funkcji mówi wprost, co zobaczy użytkownik, więc podmiana formatu na jednym
 * ekranie nie może się odbyć przez przypadek.
 *
 * Różnica wobec `utils/timeUtils.ts`: te funkcje **nie konwertują strefy
 * czasowej**. Nadają się do czasu ściennego (daty turnusów, terminy rat, daty
 * urodzenia) i do momentów, które mają być pokazane tak, jak przyszły. Moment
 * z offsetem, który ma wylądować w strefie firmy, formatuje `timeUtils`.
 *
 * Locale `pl` importujemy lokalnie — `App.tsx` rejestruje je dopiero przy
 * starcie aplikacji, a te funkcje muszą działać też w testach.
 */

const pl = (value: string) => dayjs(value).locale('pl');

/** `DD MMM YYYY`: `2019-11-09` → `09 lis 2019`. */
export const formatDayMonthYear = (value: string): string =>
  pl(value).format('DD MMM YYYY');

/** `DD MMM`: `2026-08-22` → `22 sie`. */
export const formatDayMonth = (value: string): string =>
  pl(value).format('DD MMM');

/** `DD MMM, HH:mm`: `2026-08-04T14:22:00` → `04 sie, 14:22`. */
export const formatDayMonthTime = (value: string): string =>
  pl(value).format('DD MMM, HH:mm');

/** `DD MMM YYYY, HH:mm`: `2026-08-05T12:04:00` → `05 sie 2026, 12:04`. */
export const formatDayMonthYearTime = (value: string): string =>
  pl(value).format('DD MMM YYYY, HH:mm');

/**
 * Zakres dat w zapisie `DD MMM YYYY`, skracany tam, gdzie powtórzenie nic nie
 * wnosi: `03–14 sie 2026` w obrębie jednego miesiąca, `28 lip – 03 sie 2026`
 * w obrębie roku, w innym wypadku obie daty w całości.
 */
export const formatDayMonthYearRange = (start: string, end: string): string => {
  const from = pl(start);
  const to = pl(end);

  if (from.year() === to.year() && from.month() === to.month()) {
    return `${from.format('DD')}–${to.format('DD MMM YYYY')}`;
  }
  if (from.year() === to.year()) {
    return `${from.format('DD MMM')} – ${to.format('DD MMM YYYY')}`;
  }
  return `${from.format('DD MMM YYYY')} – ${to.format('DD MMM YYYY')}`;
};
